/**
 * This code is heavily inspired by Supercluster.
 * Modifications has been made to fit EDS purposes.
 *
 * Github: https://github.com/mapbox/supercluster.
 */

import KDBush from '../kdbush/index';

const fround = Math.fround || (tmp => ((x) => { tmp[0] = +x; return tmp[0]; }))(new Float32Array(1));

function lngX(lng) {
  return lng / 360 + 0.5;
}
function latY(lat) {
  const sin = Math.sin(lat * Math.PI / 180);
  const y = (0.5 - 0.25 * Math.log((1 + sin) / (1 - sin)) / Math.PI);
  return y < 0 ? 0 : y > 1 ? 1 : y;
}
function xLng(x) {
  return (x - 0.5) * 360;
}
function yLat(y) {
  const y2 = (180 - y * 360) * Math.PI / 180;
  return 360 * Math.atan(Math.exp(y2)) / Math.PI - 90;
}

function createPointCluster(p, id) {
  const [x, y] = p.geometry.coordinates;
  return {
    x: fround(lngX(x)),
    y: fround(latY(y)),
    properties: p.properties,
    zoom: Infinity,
    index: id,
    parentId: -1
  };
}

function createCluster(x, y, id, numPoints, properties) {
  return {
    x: fround(x),
    y: fround(y),
    zoom: Infinity,
    id,
    parentId: -1,
    numPoints,
    properties
  };
}

function extend(dest, src) {
  for (const id in src) dest[id] = src[id];
  return dest;
}

function getClusterProperties(cluster) {
  const count = cluster.numPoints;
  return extend(extend({}, cluster.properties), {
    cluster: true,
    clusterID: cluster.id,
    pointCount: count
  });
}

function getClusterJSON(cluster) {
  return {
    type: 'Feature',
    id: cluster.id,
    properties: getClusterProperties(cluster),
    geometry: {
      type: 'Point',
      coordinates: [xLng(cluster.x), yLat(cluster.y)]
    }
  };
}

function getX(p) {
  return p.x;
}

function getY(p) {
  return p.y;
}

export default class Supercluster {
  constructor(options) {
    this.maxZoom = options.maxZoom;
    this.minZoom = options.minZoom;
    this.minPoints = options.minPoints;
    this.radius = options.radius;
    this.extent = options.extent;
    this.nodeSize = options.nodeSize;
    this.alerts = [];
  }

  setAlerts(alerts) {
    this.alerts = alerts;
  }

  load(nodes) {
    this.trees = new Array(this.maxZoom + 1);

    const minZoom = this.minZoom;
    const maxZoom = this.maxZoom;
    const nodeSize = this.nodeSize;

    // Map nodes to geojson.
    this.points = nodes.map(node => {
      const sumAlert = [];

      if (node.alerts && node.inactive !== true && node.static !== true) {
        node.alerts.forEach(alert => {
          sumAlert.push({
            name: alert.name,
            value: alert.value
          });
        });
      }

      return {
        type: 'Feature',
        properties: {
          key: node.key,
          nodes: [node],
          sumAlert
        },
        geometry: {
          type: 'Point',
          coordinates: [
            node.x,
            node.y
          ]
        }
      };
    });

    let clusters = [];
    for (let i = 0; i < this.points.length; i += 1) {
      if (!this.points[i].geometry) continue;
      clusters.push(createPointCluster(this.points[i], i));
    }
    this.trees[maxZoom + 1] = new KDBush(clusters, getX, getY, nodeSize, Float32Array);

    for (let z = maxZoom; z >= minZoom; z -= 1) {
      clusters = this.cluster(clusters, z);
      this.trees[z] = new KDBush(clusters, getX, getY, nodeSize, Float32Array);
    }
  }

  limitZoom(z) {
    return Math.max(this.minZoom, Math.min(+z, this.maxZoom + 1));
  }

  cluster(points, zoom) {
    const clusters = [];
    const radius = this.radius;
    const extent = this.extent;
    const minPoints = this.minPoints;
    const r = radius / (extent * Math.pow(2, zoom));

    for (let i = 0; i < points.length; i += 1) {
      const p = points[i];
      // if we've already visited the point at this zoom level, skip it
      if (p.zoom <= zoom) continue;
      p.zoom = zoom;

      // find all nearby points
      const tree = this.trees[zoom + 1];
      const neighborIds = tree.within(p.x, p.y, r);

      const numPointsOrigin = p.numPoints || 1;
      let numPoints = numPointsOrigin;

      // count the number of points in a potential cluster
      let includedNodes = p.properties.nodes;
      for (const neighborId of neighborIds) {
        const b = tree.points[neighborId];
        // filter out neighbors that are already processed
        if (b.zoom > zoom) {
          numPoints += b.numPoints || 1;
          // Get all the child nodes.
          includedNodes = [...includedNodes, ...b.properties.nodes];
        }
      }

      // enough points to form a cluster
      if (numPoints >= minPoints) {
        let wx = p.x * numPointsOrigin;
        let wy = p.y * numPointsOrigin;

        // encode both zoom and point index on which the cluster originated -- offset by total length of features
        const id = (i * 32) + (zoom + 1) + this.points.length;

        for (const neighborId of neighborIds) {
          const b = tree.points[neighborId];

          if (b.zoom <= zoom) continue;
          // save the zoom (so it doesn't get processed twice)
          b.zoom = zoom;

          const numPoints2 = b.numPoints || 1;
          // accumulate coordinates for calculating weighted center
          wx += b.x * numPoints2;
          wy += b.y * numPoints2;

          b.parentId = id;
        }

        // count the number of different alert nodes
        const sumAlertMap = {};
        includedNodes.forEach(node => {
          if (node.alerts && node.alerts.length > 0 && node.inactive !== true && node.static !== true) {
            const maxAlertName = this.maxAlertName(node);
            if (maxAlertName) {
              if (sumAlertMap[maxAlertName]) {
                sumAlertMap[maxAlertName] += 1;
              } else {
                sumAlertMap[maxAlertName] = 1;
              }
            }
          }
        });

        const sumAlert = [];
        if (this.alerts && this.alerts.length > 0) {
          this.alerts.forEach(alert => {
            if (sumAlertMap[alert.name]) {
              sumAlert.push({
                name: alert.name,
                value: sumAlertMap[alert.name]
              });
            }
          });
        }

        const clusterProperties = {
          nodes: includedNodes,
          sumAlert
        };

        p.parentId = id;
        clusters.push(createCluster(wx / numPoints, wy / numPoints, id, numPoints, clusterProperties));
      } else {
        // left points as unclustered
        clusters.push(p);

        if (numPoints > 1) {
          for (const neighborId of neighborIds) {
            const b = tree.points[neighborId];
            if (b.zoom <= zoom) continue;
            b.zoom = zoom;
            clusters.push(b);
          }
        }
      }
    }

    return clusters;
  }

  getClusters(bbox, zoom) {
    let minLng = ((bbox[0] + 180) % 360 + 360) % 360 - 180;
    const minLat = Math.max(-90, Math.min(90, bbox[1]));
    let maxLng = bbox[2] === 180 ? 180 : ((bbox[2] + 180) % 360 + 360) % 360 - 180;
    const maxLat = Math.max(-90, Math.min(90, bbox[3]));

    if (bbox[2] - bbox[0] >= 360) {
      minLng = -180;
      maxLng = 180;
    } else if (minLng > maxLng) {
      const easternHem = this.getClusters([minLng, minLat, 180, maxLat], zoom);
      const westernHem = this.getClusters([-180, minLat, maxLng, maxLat], zoom);
      return easternHem.concat(westernHem);
    }

    const tree = this.trees[this.limitZoom(zoom)];
    const ids = tree.range(lngX(minLng), latY(maxLat), lngX(maxLng), latY(minLat));
    const clusters = [];
    for (const id of ids) {
      const c = tree.points[id];
      clusters.push(c.numPoints ? getClusterJSON(c) : this.points[c.index]);
    }
    return clusters;
  }

  getClusterExpansionZoom(clusterId) {
    let expansionZoom = this._getOriginZoom(clusterId) - 1;
    while (expansionZoom <= this.maxZoom) {
      const children = this.getChildren(clusterId);
      expansionZoom += 1;
      if (children.length !== 1) break;
      clusterId = children[0].properties.clusterID;
    }
    return expansionZoom;
  }

  getChildren(clusterId) {
    const originId = this._getOriginId(clusterId);
    const originZoom = this._getOriginZoom(clusterId);
    const errorMsg = 'No cluster with the specified id.';

    const index = this.trees[originZoom];
    if (!index) throw new Error(errorMsg);

    const origin = index.points[originId];
    if (!origin) throw new Error(errorMsg);

    const r = this.radius / ((this.extent + 1) * Math.pow(2, originZoom - 1));
    const ids = index.within(origin.x, origin.y, r);
    const children = [];
    for(const id of ids) {
      const c = index.points[id];
      if(c.parentId === clusterId) {
        children.push(c.numPoints ? getClusterJSON(c) : this.points[c.index]);
      }
    }

    if (children.length === 0) throw new Error(errorMsg);

    return children;
  }

  _getOriginId(clusterId) {
    return Math.floor((clusterId - this.points.length) / 32);
  }

  _getOriginZoom(clusterId) {
    return (clusterId - this.points.length) % 32;
  }

  maxAlertName(node) {
    if (node.alerts && node.alerts.length > 0) {
      const alertMap = {};
      node.alerts.forEach(alert => alertMap[alert.name] = true);

      for (let i = 0; i < this.alerts.length; i += 1) {
        if (alertMap[this.alerts[i].name]) {
          return this.alerts[i].name;
        }
      }
    }
    return '';
  }
}
